import React, { useMemo, useState } from 'react';

import styled from '@emotion/styled';

import { Box, COLORS, Text, mq } from '@clutter/clean';

import {
  Location__City,
  Location__Service,
  Location__State,
} from '@graphql/platform';

import { cityURL } from '@root/components/locations/routes';

import { useIsomorphicBreakpoints } from '@utils/hooks/use_breakpoints';

import { LocationLinksLayout } from './location_links_layout';

const MOBILE_PAGE_SIZE = 6;

const Title = styled(Text.Title)`
  ${mq({
    fontSize: ['40px !important', null, null],
    lineHeight: ['120% !important', null, null],
  })}
`;

const PaginationDots = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  margin-top: 36px;
`;

const Dot = styled.button<{ active: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${({ active }) =>
    active ? COLORS.tealPrimary : COLORS.grayBorder};
  transition: background 0.2s ease;
`;

export const CityLinks: React.FC<{
  states: ReadonlyArray<
    Pick<Location__State, 'id' | 'code' | 'name' | 'slug'> & {
      cities: ReadonlyArray<
        Pick<Location__City, 'id' | 'content' | 'name' | 'services' | 'slug'>
      >;
    }
  >;
  service: 'storage' | 'moving';
}> = ({ states, service }) => {
  const { isMobile } = useIsomorphicBreakpoints();
  const [currentPage, setCurrentPage] = useState(0);

  const locationService =
    service === 'moving'
      ? Location__Service.Moving
      : Location__Service.SmartStorage;

  const title =
    service === 'moving'
      ? `Our Local Moving Locations`
      : `Our storage locations`;

  const allCities = useMemo(
    () =>
      states.flatMap((state) =>
        state.cities
          .filter((city) => city.services.includes(locationService))
          .map((city) => ({
            id: city.id,
            name: city.name,
            stateCode: state.code,
            url: cityURL(state.slug, city.slug, service),
          })),
      ),
    [states, locationService, service],
  );

  const totalPages = Math.ceil(allCities.length / MOBILE_PAGE_SIZE);
  const mobileCities = allCities.slice(
    currentPage * MOBILE_PAGE_SIZE,
    (currentPage + 1) * MOBILE_PAGE_SIZE,
  );

  return (
    <Box textAlign="center">
      <Box margin={['0 0 36px', '0 0 72px']}>
        <Title size="large" tag="h2">
          {title}
        </Title>
      </Box>
      {isMobile ? (
        <>
          <LocationLinksLayout>
            {mobileCities.map((city) => (
              <LocationLinksLayout.Link key={city.id} to={city.url}>
                {city.name}, {city.stateCode}
              </LocationLinksLayout.Link>
            ))}
          </LocationLinksLayout>
          {totalPages > 1 && (
            <PaginationDots>
              {Array.from({ length: totalPages }, (_, i) => (
                <Dot
                  key={i}
                  active={i === currentPage}
                  onClick={() => setCurrentPage(i)}
                  aria-label={`Page ${i + 1} of ${totalPages}`}
                />
              ))}
            </PaginationDots>
          )}
        </>
      ) : (
        <LocationLinksLayout>
          {states.map((state) => (
            <React.Fragment key={state.id}>
              {state.cities
                .filter((city) => city.services.includes(locationService))
                .map((city) => (
                  <LocationLinksLayout.Link
                    key={city.id}
                    to={cityURL(state.slug, city.slug, service)}
                  >
                    {city.name}, {state.code}
                  </LocationLinksLayout.Link>
                ))}
            </React.Fragment>
          ))}
        </LocationLinksLayout>
      )}
    </Box>
  );
};
